import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from './config';
import { logout } from './authSlice';

const initialState = {
  collaborations: [],
  collaboration: null,
  activeCollabId: null,
  // Note de fin de contrat : critères de la zone d'activité notés ({ key, label, score }).
  ratingCriteria: [],
  averageRating: 0, // Initialized to zero
  loading: false,
  isSuccess: false,
  message: '',
  errors: [],
  users: [],
  availableStaff: [],
  availableStaffTotal: 0,
  staffContractHistory: [],
  staffMonthlyReviews: [],
  staffAllMonthlyReviews: [],
  staffRatings: [],
  staffRatingAverage: { average: 0, count: 0 },
  endedStaffRatings: [], // one page — GET /collaboration/company/ended-staff-ratings
  endedStaffRatingsTotal: 0,
  endedStaffRatingsLoading: false,
  recordsTotal: 0, // Initialize recordsTotal if it's part of the state
  combinedData: [], // To handle real-time updates for chat messages and files
  // Server-side pagination for the admin "Collaborations Sent" table - kept
  // separate from `collaborations`/`recordsTotal` above, which hold the full
  // array used by attendance/messenger/staff-details/sidebar badge.
  paginatedSentCollaborations: [],
  sentRecordsTotal: 0,
  sentFetchLoading: false,
  sentStatusCounts: { working: 0, ended: 0 },
  // Server-side pagination for the admin "Received Applications" table.
  paginatedReceivedCollaborations: [],
  receivedRecordsTotal: 0,
  receivedFetchLoading: false,
  receivedStatusCounts: { Pending: 0, Accepted: 0, Rejected: 0 },
  receivedSourceCounts: { all: 0, spontaneous: 0, jobPost: 0 },
  receivedRoleOptions: []
};

// Async actions
export const fetchSentCollaborations = createAsyncThunk(
  'collaborations/fetchCollaborations',
  async (params, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get('/collaboration/collaborationsSend', {
        params
      });
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        dispatch(logout());
      }
      if (!error.response) {
        console.error('Error:', error);
        throw error;
      }
      console.error('Error Response:', error.response.data);
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchSentCollaborationsPaginated = createAsyncThunk(
  'collaborations/fetchSentCollaborationsPaginated',
  async (params, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get('/collaboration/collaborationsSend/paginated', { params });
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchSentCollaborationsCounts = createAsyncThunk(
  'collaborations/fetchSentCollaborationsCounts',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get('/collaboration/collaborationsSend/counts');
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchReciveidCollaborations = createAsyncThunk(
  'collaborations/fetchRCollaborations',
  async (params, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get('/collaboration/recived', { params });
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        dispatch(logout());
      }
      if (!error.response) {
        console.error('Error:', error);
        throw error;
      }
      console.error('Error Response:', error.response.data);
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchReceivedCollaborationsPaginated = createAsyncThunk(
  'collaborations/fetchReceivedCollaborationsPaginated',
  async (params, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get('/collaboration/recived/paginated', { params });
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchReceivedCollaborationsCounts = createAsyncThunk(
  'collaborations/fetchReceivedCollaborationsCounts',
  async (params, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get('/collaboration/recived/counts', { params });
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchAvailableStaff = createAsyncThunk(
  'collaborations/fetchAvailableStaff',
  async (params, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get('/company/available-staff', { params });
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        dispatch(logout());
      }
      if (!error.response) {
        console.error('Error:', error);
        throw error;
      }
      console.error('Error Response:', error.response.data);
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchStaffContractHistory = createAsyncThunk(
  'collaborations/fetchStaffContractHistory',
  async (staffId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/collaboration/staff-history/${staffId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching staff contract history:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchStaffMonthlyReviews = createAsyncThunk(
  'collaborations/fetchStaffMonthlyReviews',
  async (staffId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/staff-monthly-review/${staffId}/history`);
      return response.data;
    } catch (error) {
      console.error('Error fetching staff monthly reviews:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchStaffAllMonthlyReviews = createAsyncThunk(
  'collaborations/fetchStaffAllMonthlyReviews',
  async (staffId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/staff-monthly-review/${staffId}/all-history`);
      return response.data;
    } catch (error) {
      console.error('Error fetching staff all-company monthly reviews:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchStaffRatings = createAsyncThunk(
  'collaborations/fetchStaffRatings',
  async (staffId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/staff-rating/${staffId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching staff ratings:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchStaffRatingAverage = createAsyncThunk(
  'collaborations/fetchStaffRatingAverage',
  async (staffId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/staff-rating/${staffId}/average`);
      return response.data;
    } catch (error) {
      console.error('Error fetching staff rating average:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const rejectCollaborationSent = createAsyncThunk(
  'collaborations/rejectCollaborationSent',
  async (id, { rejectWithValue }) => {
    try {
      await api.put(`/collaboration/${id}/rejectsent`);
    } catch (error) {
      console.error('Error rejecting collaboration:', error);
      return rejectWithValue('Error rejecting collaboration');
    }
  }
);

export const acceptCollaborationSent = createAsyncThunk(
  'collaborations/acceptCollaborationSent',
  async (id, { rejectWithValue }) => {
    try {
      await api.put(`/collaboration/${id}/acceptsent`);
    } catch (error) {
      console.error('Error accepting collaboration:', error);
      return rejectWithValue('Error accepting collaboration');
    }
  }
);

export const getprofileuser = createAsyncThunk(
  'user/getprofile',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/user/getUser/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error get profile:', error);
      return rejectWithValue('Error get profile');
    }
  }
);

export const sendContract = createAsyncThunk(
  'collaborations/sendContract',
  async (sendCollaborationDTO) => {
    try {
      const response = await api.post(
        '/collaboration/send',
        sendCollaborationDTO
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }
);

export const fetchExperience = createAsyncThunk(
  'collaborations/fetchExperience',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/experience/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching experience:', error);
      throw rejectWithValue('Failed to fetch experience');
    }
  }
);

export const fetchSkills = createAsyncThunk(
  'collaborations/fetchSkills',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/skills/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching skills:', error);
      throw rejectWithValue('Failed to fetch skills');
    }
  }
);

export const fetchHobbies = createAsyncThunk(
  'collaborations/fetchHobbies',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/hobbies/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching hobbies:', error);
      throw rejectWithValue('Failed to fetch hobbies');
    }
  }
);

export const fetchEducation = createAsyncThunk(
  'collaborations/fetchEducation',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/education/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching education:', error);
      throw rejectWithValue('Failed to fetch education');
    }
  }
);

export const fetchLanguages = createAsyncThunk(
  'collaborations/fetchLanguages',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/languages/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching languages:', error);
      throw rejectWithValue('Failed to fetch languages');
    }
  }
);

export const addFiles = createAsyncThunk(
  'collaborations/addFiles',
  async ({ id, files, token }, { rejectWithValue }) => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });

    try {
      const response = await api.post(
        `/collaboration/${id}/add-files`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`
          }
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        return rejectWithValue(error.response.data);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const addMessage = createAsyncThunk(
  'collaborations/addMessage',
  async ({ id, message, token }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/collaboration/${id}/add-message`,
        { message },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        return rejectWithValue(error.response.data);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const addRating = createAsyncThunk(
  'collaborations/addRating',
  async (
    // `criteria` : [{ key, score }], une note par critère de la zone d'activité.
    { employeeId, criteria, collaborationId },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response = await api.post(
        `/collaboration/${employeeId}/${collaborationId}/rate`,
        { criteria }
      );
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        dispatch(logout());
      }
      if (!error.response) {
        console.error('Error:', error);
        throw error;
      }
      console.error('Error Response:', error.response.data);
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchEndedStaffRatings = createAsyncThunk(
  'collaborations/fetchEndedStaffRatings',
  // params: { page, limit, search }
  async (params, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get('/collaboration/company/ended-staff-ratings', { params });
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        dispatch(logout());
      }
      return rejectWithValue(
        error.response ? error.response.data : error.message
      );
    }
  }
);

export const fetchRating = createAsyncThunk(
  'collaborations/fetchRating',
  // Accepts either a bare employeeId (most callers — "their latest rating"), or
  // { employeeId, collaborationId } when the caller knows which contract it wants.
  async (arg, { rejectWithValue, dispatch }) => {
    const employeeId = typeof arg === 'string' ? arg : arg?.employeeId;
    const collaborationId = typeof arg === 'string' ? undefined : arg?.collaborationId;
    try {
      const response = await api.get(`/collaboration/${employeeId}/rating`, {
        params: collaborationId ? { collaborationId } : undefined
      });
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        dispatch(logout());
      }
      return rejectWithValue(
        error.response ? error.response.data : error.message
      );
    }
  }
);

export const acceptRequest = createAsyncThunk(
  'collaborations/acceptRequest',
  async ({ collabId, itemId, itemType }, { rejectWithValue }) => {
    try {
      const response = await api.put(
        `/collaboration/${collabId}/${itemType}/${itemId}/accept`
      );
      return response.data;
    } catch (error) {
      console.error('Error accepting request:', error);
      return rejectWithValue(
        error.response ? error.response.data : error.message
      );
    }
  }
);

export const RefuseRequest = createAsyncThunk(
  'collaborations/refuseRequest',
  async ({ collabId, itemId, itemType }, { rejectWithValue }) => {
    try {
      const response = await api.put(
        `/collaboration/${collabId}/${itemType}/${itemId}/refuse`
      );
      return response.data;
    } catch (error) {
      console.error('Error refusing request:', error);
      return rejectWithValue(
        error.response ? error.response.data : error.message
      );
    }
  }
);

export const fetchDiseaseImage = createAsyncThunk(
  'collaborations/fetchDiseaseImage',
  async (diseaseId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/collaboration/disease/${diseaseId}`, {
        responseType: 'blob'
      });
      return URL.createObjectURL(response.data);
    } catch (error) {
      console.error('Error fetching disease image:', error);
      return rejectWithValue(
        error.response ? error.response.data : error.message
      );
    }
  }
);

export const addAbsenceToday = createAsyncThunk(
  'collaborations/addAbsenceToday',
  async ({ id, token }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post(
        `/collaboration/${id}/add-absence-today`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        dispatch(logout());
      }
      if (!error.response) {
        console.error('Error:', error);
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);
export const fetchCollaborationById = createAsyncThunk(
  'collaborations/fetchCollaborationById',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get(`/collaboration/SendCollaborations/${id}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const patchWeeklyOffDays = createAsyncThunk(
  'collaborations/patchWeeklyOffDays',
  async ({ collabId, weeklyOffDays }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.patch(
        `/collaboration/${collabId}/weekly-off-days`,
        { weeklyOffDays }
      );
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const addPublicHoliday = createAsyncThunk(
  'collaborations/addPublicHoliday',
  async ({ collabId, date, label }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post(
        `/collaboration/${collabId}/public-holidays`,
        { date, label }
      );
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deletePublicHoliday = createAsyncThunk(
  'collaborations/deletePublicHoliday',
  async ({ collabId, holidayId }, { rejectWithValue, dispatch }) => {
    try {
      await api.delete(
        `/collaboration/${collabId}/public-holidays/${holidayId}`
      );
      return holidayId;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const endCollaboration = createAsyncThunk(
  'collaboration/endCollaboration',
  async (collabId, { rejectWithValue }) => {
    try {
      const response = await api.put(`/collaboration/${collabId}/end`);
      return response.data;
    } catch (error) {
      console.error('Error ending collaboration:', error);
      return rejectWithValue(error.response.data);
    }
  }
);


// Slice
const collaborationSlice = createSlice({
  name: 'collaborations',
  initialState,
  reducers: {
    updateCombinedData: (state, action) => {
      state.combinedData.push(action.payload.newMessage);
      state.combinedData.sort(
        (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
      );
    },
    setActiveCollabId: (state, action) => {
      const collabId = action.payload;
      state.activeCollabId = collabId;
      if (collabId) {
        state.collaborations = state.collaborations.map((c) =>
          c._id === collabId ? { ...c, adminUnreadCount: 0 } : c
        );
      }
    },
    incrementCollabUnread: (state, action) => {
      const collabId = action.payload;
      if (!collabId || collabId === state.activeCollabId) return;
      state.collaborations = state.collaborations.map((c) =>
        c._id === collabId ? { ...c, adminUnreadCount: (c.adminUnreadCount || 0) + 1 } : c
      );
    },
  },
  extraReducers: {
    [fetchSentCollaborations.pending]: (state) => {
      state.loading = true;
    },
    [fetchSentCollaborations.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      const newList = Array.isArray(payload) ? payload : (payload?.data || []);
      // Preserve any unread count bumped locally by the real-time socket
      // listener if it's higher than what this fetch returned (avoids a
      // race where an in-flight request resolves with a stale count and
      // wipes out the "new message" badge).
      const prevById = new Map(state.collaborations.map((c) => [c._id, c]));
      state.collaborations = newList.map((c) => {
        const prev = prevById.get(c._id);
        const prevUnread = prev?.adminUnreadCount || 0;
        const nextUnread = c.adminUnreadCount || 0;
        return prevUnread > nextUnread ? { ...c, adminUnreadCount: prevUnread } : c;
      });
      state.recordsTotal = payload?.recordsTotal || 0;
      state.loading = false;
    },
    [fetchSentCollaborationsPaginated.pending]: (state) => {
      state.sentFetchLoading = true;
    },
    [fetchSentCollaborationsPaginated.fulfilled]: (state, { payload }) => {
      state.paginatedSentCollaborations = payload?.data || [];
      state.sentRecordsTotal = payload?.recordsTotal || 0;
      state.sentFetchLoading = false;
    },
    [fetchSentCollaborationsPaginated.rejected]: (state) => {
      state.sentFetchLoading = false;
    },
    [fetchSentCollaborationsCounts.fulfilled]: (state, { payload }) => {
      state.sentStatusCounts = { working: payload?.working || 0, ended: payload?.ended || 0 };
    },
    [fetchReceivedCollaborationsPaginated.pending]: (state) => {
      state.receivedFetchLoading = true;
    },
    [fetchReceivedCollaborationsPaginated.fulfilled]: (state, { payload }) => {
      state.paginatedReceivedCollaborations = payload?.data || [];
      state.receivedRecordsTotal = payload?.recordsTotal || 0;
      state.receivedFetchLoading = false;
    },
    [fetchReceivedCollaborationsPaginated.rejected]: (state) => {
      state.receivedFetchLoading = false;
    },
    [fetchReceivedCollaborationsCounts.fulfilled]: (state, { payload }) => {
      state.receivedStatusCounts = payload?.statusCounts || { Pending: 0, Accepted: 0, Rejected: 0 };
      state.receivedSourceCounts = payload?.sourceCounts || { all: 0, spontaneous: 0, jobPost: 0 };
      state.receivedRoleOptions = payload?.roleOptions || [];
    },
    [fetchSentCollaborations.rejected]: (state, { payload }) => {
      if (payload?.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },
    [fetchReciveidCollaborations.pending]: (state) => {
      state.loading = true;
    },
    [fetchReciveidCollaborations.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.collaborations = payload?.data || [];
      state.recordsTotal = payload?.recordsTotal || 0;
      state.loading = false;
    },
    [fetchReciveidCollaborations.rejected]: (state, { payload }) => {
      if (payload?.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },
    [fetchAvailableStaff.pending]: (state) => {
      state.loading = true;
    },
    [fetchAvailableStaff.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.availableStaff = Array.isArray(payload) ? payload : payload?.data || [];
      state.availableStaffTotal = Array.isArray(payload) ? payload.length : payload?.recordsTotal || 0;
      state.loading = false;
    },
    [fetchAvailableStaff.rejected]: (state, { payload }) => {
      if (payload?.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },
    [fetchStaffContractHistory.fulfilled]: (state, { payload }) => {
      state.staffContractHistory = Array.isArray(payload) ? payload : [];
    },
    [fetchStaffContractHistory.rejected]: (state) => {
      state.staffContractHistory = [];
    },
    [fetchStaffMonthlyReviews.fulfilled]: (state, { payload }) => {
      state.staffMonthlyReviews = Array.isArray(payload) ? payload : [];
    },
    [fetchStaffMonthlyReviews.rejected]: (state) => {
      state.staffMonthlyReviews = [];
    },
    [fetchStaffAllMonthlyReviews.fulfilled]: (state, { payload }) => {
      state.staffAllMonthlyReviews = Array.isArray(payload) ? payload : [];
    },
    [fetchStaffAllMonthlyReviews.rejected]: (state) => {
      state.staffAllMonthlyReviews = [];
    },
    [fetchStaffRatings.fulfilled]: (state, { payload }) => {
      state.staffRatings = Array.isArray(payload) ? payload : [];
    },
    [fetchStaffRatings.rejected]: (state) => {
      state.staffRatings = [];
    },
    [fetchStaffRatingAverage.fulfilled]: (state, { payload }) => {
      state.staffRatingAverage = payload || { average: 0, count: 0 };
    },
    [fetchStaffRatingAverage.rejected]: (state) => {
      state.staffRatingAverage = { average: 0, count: 0 };
    },
    [acceptCollaborationSent.pending]: (state) => {
      state.loading = true;
    },
    [acceptCollaborationSent.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.collaborations = payload?.data || [];
      state.recordsTotal = payload?.recordsTotal || 0;
      state.loading = false;
    },
    [acceptCollaborationSent.rejected]: (state, { payload }) => {
      if (payload?.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },
    [rejectCollaborationSent.pending]: (state) => {
      state.loading = true;
    },
    [rejectCollaborationSent.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.collaborations = payload?.data || [];
      state.recordsTotal = payload?.recordsTotal || 0;
      state.loading = false;
    },
    [rejectCollaborationSent.rejected]: (state, { payload }) => {
      if (payload?.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },
    [sendContract.pending]: (state) => {
      state.loading = true;
    },
    [sendContract.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.collaborations = payload?.data || [];
      state.recordsTotal = payload?.recordsTotal || 0;
      state.loading = false;
    },
    [sendContract.rejected]: (state, { payload }) => {
      if (payload?.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },
    [fetchExperience.pending]: (state) => {
      state.loading = true;
    },
    [fetchExperience.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.collaborations = payload?.data || [];
      state.recordsTotal = payload?.recordsTotal || 0;
      state.loading = false;
    },
    [fetchExperience.rejected]: (state, { payload }) => {
      if (payload?.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },
    [fetchEducation.pending]: (state) => {
      state.loading = true;
    },
    [fetchEducation.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.collaborations = payload?.data || [];
      state.recordsTotal = payload?.recordsTotal || 0;
      state.loading = false;
    },
    [fetchEducation.rejected]: (state, { payload }) => {
      if (payload?.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },
    [getprofileuser.pending]: (state) => {
      state.loading = true;
    },
    [getprofileuser.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.collaborations = payload?.data || [];
      state.recordsTotal = payload?.recordsTotal || 0;
      state.loading = false;
    },
    [getprofileuser.rejected]: (state, { payload }) => {
      if (payload?.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },
    [fetchSkills.pending]: (state) => {
      state.loading = true;
    },
    [fetchSkills.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.collaborations = payload?.data || [];
      state.recordsTotal = payload?.recordsTotal || 0;
      state.loading = false;
    },
    [fetchSkills.rejected]: (state, { payload }) => {
      if (payload?.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },
    [fetchHobbies.pending]: (state) => {
      state.loading = true;
    },
    [fetchHobbies.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.collaborations = payload?.data || [];
      state.recordsTotal = payload?.recordsTotal || 0;
      state.loading = false;
    },
    [fetchHobbies.rejected]: (state, { payload }) => {
      if (payload?.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },
    [fetchLanguages.pending]: (state) => {
      state.loading = true;
    },
    [fetchLanguages.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.collaborations = payload?.data || [];
      state.recordsTotal = payload?.recordsTotal || 0;
      state.loading = false;
    },
    [fetchLanguages.rejected]: (state, { payload }) => {
      if (payload?.statusCode === 400) {
        state.errors = payload?.message;
      }
      state.isSuccess = false;
      state.loading = false;
    },
    [addFiles.pending]: (state) => {
      state.loading = true;
    },
    [addFiles.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.loading = false;
    },
    [addFiles.rejected]: (state, { payload }) => {
      state.errors = payload;
      state.isSuccess = false;
      state.loading = false;
    },
    [addMessage.pending]: (state) => {
      state.loading = true;
    },
    [addMessage.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.loading = false;
    },
    [addMessage.rejected]: (state, { payload }) => {
      state.errors = payload;
      state.isSuccess = false;
      state.loading = false;
    },
    [addRating.pending]: (state) => {
      state.loading = true;
    },
    [addRating.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.collaborations = payload || [];
      state.loading = false;
    },
    [addRating.rejected]: (state, { payload }) => {
      state.errors = payload;
      state.isSuccess = false;
      state.loading = false;
    },
    [fetchRating.pending]: (state) => {
      state.loading = true;
    },
    [fetchRating.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.ratingCriteria = payload.criteria || [];
      state.averageRating = payload.averageRating;
      state.loading = false;
    },
    [fetchRating.rejected]: (state, { payload }) => {
      state.errors = payload;
      state.isSuccess = false;
      state.loading = false;
    },
    [fetchEndedStaffRatings.pending]: (state) => {
      state.endedStaffRatingsLoading = true;
    },
    [fetchEndedStaffRatings.fulfilled]: (state, { payload }) => {
      state.endedStaffRatings = payload.data || [];
      state.endedStaffRatingsTotal = payload.recordsTotal || 0;
      state.endedStaffRatingsLoading = false;
    },
    [fetchEndedStaffRatings.rejected]: (state, { payload }) => {
      state.errors = payload;
      state.endedStaffRatingsLoading = false;
    },
    [acceptRequest.pending]: (state) => {
      state.loading = true;
    },
    [acceptRequest.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.loading = false;
      // Optionally, update the collaboration state here
    },
    [acceptRequest.rejected]: (state, { payload }) => {
      state.errors = payload;
      state.isSuccess = false;
      state.loading = false;
    },
    [RefuseRequest.pending]: (state) => {
      state.loading = true;
    },
    [RefuseRequest.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.loading = false;
    },
    [RefuseRequest.rejected]: (state, { payload }) => {
      state.errors = payload;
      state.isSuccess = false;
      state.loading = false;
    },
    [fetchDiseaseImage.pending]: (state) => {
      state.loading = true;
    },
    [fetchDiseaseImage.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.loading = false;
      state.diseaseImage = payload;
    },
    [fetchDiseaseImage.rejected]: (state, { payload }) => {
      state.errors = payload;
      state.isSuccess = false;
      state.loading = false;
    },
    [addAbsenceToday.pending]: (state) => {
      state.loading = true;
    },
    [addAbsenceToday.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.loading = false;
    },
    [addAbsenceToday.rejected]: (state, { payload }) => {
      state.errors = payload;
      state.isSuccess = false;
      state.loading = false;
    },
    [endCollaboration.pending]: (state) => {
      state.loading = true;
    },
    [endCollaboration.fulfilled]: (state, { payload }) => {
      state.isSuccess = true;
      state.loading = false;
    },
    [endCollaboration.rejected]: (state, { payload }) => {
      state.errors = payload;
      state.isSuccess = false;
      state.loading = false;
    },
  }
});

export const { updateCombinedData, setActiveCollabId, incrementCollabUnread } = collaborationSlice.actions;
export default collaborationSlice.reducer;
